import { Drawer, Tooltip } from 'antd'
import 'antd/dist/antd.css'
import { FC, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import './chatbot.less'
import { useChatbotToken, useChatbotUrl } from './Queries'

const TOKEN_NAME = 'alyssaToken'
const TOKEN_EXPIRED_EVENT_NAME = 'alyssa-token-expired'
const TOKEN_UPDATED_EVENT_NAME = 'alyssa-token-updated'
const IS_ALYSSA_OPEN = '__is_alyssa_open__'
const CLOSE_EVENT_NAME = 'alyssa-chat-close'

export const ChatbotContainer: FC<{ setCollapsed: (collapsed: boolean) => void }> = ({ setCollapsed }) => {
  const [open, setOpen] = useState<boolean>(true)
  const [t] = useTranslation()
  const { data: tokenData, error: tokenError, loading: tokenLoading, refetch: refetchToken } = useChatbotToken()
  const { data: urlData, error: urlError, loading: urlLoading } = useChatbotUrl()

  const url = urlData && !urlError && !urlLoading && urlData.retrieveChatbotUrl ? urlData.retrieveChatbotUrl : null

  function setToken(token: string) {
    sessionStorage.setItem(TOKEN_NAME, token)
  }

  if (tokenData && !tokenError && !tokenLoading && tokenData.retrieveChatbotToken) {
    setToken(tokenData.retrieveChatbotToken)
  }

  const refreshToken = async (): Promise<void> => {
    const queryResult = await refetchToken()
    if (queryResult.data.retrieveChatbotToken) {
      setToken(queryResult.data.retrieveChatbotToken)
    }
  }

  document.addEventListener(TOKEN_EXPIRED_EVENT_NAME, async () => {
    await refreshToken()
    document.dispatchEvent(
      new CustomEvent(TOKEN_UPDATED_EVENT_NAME, {
        bubbles: true,
        cancelable: false,
        composed: true,
      }),
    )
  })

  useEffect(() => {
    if (tokenData && tokenData.retrieveChatbotToken && url) {
      if (!document.getElementById('alyssa-auto')) {
        const script = document.createElement('script')
        script.id = 'alyssa-auto'
        script.src = url + '/alyssa-auto.js'
        script.setAttribute('data-position', 'custom')
        script.setAttribute('data-button-icon-width', '32px')
        script.setAttribute('data-button-toggle-chat', 'false')
        script.setAttribute('data-start-closed', 'false')
        script.setAttribute('data-token-name', TOKEN_NAME)
        document.body.appendChild(script)
      }
    }
  }, [tokenData, url])

  // Alyssa's close button closes the panel: Alyssa must not hide itself
  useEffect(() => {
    const onAlyssaClose = (event: Event) => {
      event.preventDefault()
      setOpen(false)
    }
    document.addEventListener(CLOSE_EVENT_NAME, onAlyssaClose)
    return () => document.removeEventListener(CLOSE_EVENT_NAME, onAlyssaClose)
  }, [])

  const toggleDrawer = () => {
    if (open) {
      onClose()
    } else {
      setOpen(true)
    }
  }

  const onClose = () => {
    setOpen(false)
  }

  useEffect(() => {
    const wasOpen = localStorage.getItem(IS_ALYSSA_OPEN)
    if (wasOpen !== null) {
      setOpen(JSON.parse(wasOpen))
    }
  }, [setOpen])

  useEffect(() => {
    setCollapsed(open)
    localStorage.setItem(IS_ALYSSA_OPEN, JSON.stringify(open))
  }, [open, setCollapsed])

  return (
    <div className="chatbot-button">
      <Tooltip title={t('navigation.tooltip.alyssa')}>
        <button className={'per-btnCircle per-btnCircle--transparent-variant'} type="button" onClick={toggleDrawer}>
          <div id="alyssa-button-placeholder" />
        </button>
      </Tooltip>
      {/* the header (title, New chat, close button) is Alyssa's own */}
      <Drawer
        placement="right"
        closable={false}
        onClose={onClose}
        visible={open}
        mask={false}
        maskClosable={false}
        getContainer="main"
        className="chatbot-container"
      >
        <div className="fullHeight">
          <div id="alyssa-chat-placeholder" />
        </div>
      </Drawer>
    </div>
  )
}
